import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { Link, useParams } from "react-router-dom";
import { SolutionBounty } from "@/types/bounty";

const SolutionBounties = () => {
  const { userId } = useParams();

  // Shared patent bounty definition
  const sharedPatentBounty: SolutionBounty = {
    id: 999,
    title: "iSPAI Provisional Patent Approved",
    date: "2024-01-10",
    status: "Accepted",
    bountyAmount: 2500,
    isShared: true,
    collaborators: [
      {
        name: "Trey Fleming",
        title: "iSPAI Founder",
        profileUrl: "/myspai-profile/trey-fleming",
        amount: 2500
      },
      {
        name: "Corey Vickers",
        title: "iSPAI Chief Creator",
        profileUrl: "/myspai-profile/corey-vickers",
        amount: 2500
      },
      {
        name: "Miguel Escobedo",
        title: "iSPAI Chief Product Officer",
        profileUrl: "/myspai-profile/miguel-escobedo",
        amount: 2500
      },
      {
        name: "Yannis Kalfoglou, PhD",
        title: "iSPAI Chief Technical Advisor",
        profileUrl: "/myspai-profile/yannis-kalfoglou",
        amount: 2500
      }
    ]
  };

  // Chris Clayton solution bounties
  const chrisBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "iSPAI Product Pricing Model",
      date: "2024-03-10",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 2,
      title: "iSPAI Alliance Partner Strategy",
      date: "2024-02-25",
      status: "Accepted",
      bountyAmount: 2000
    },
    {
      id: 3,
      title: "iSPAI's Business Case Framework",
      date: "2024-02-05",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 4,
      title: "iSPAI's GTM Alliance with Gryphon Citadel",
      date: "2024-01-20",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Incentives for Gryphon Citadel"
    }
  ];

  // Trey Fleming solution bounties
  const treyBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "iSPAI Founding Vision and Concept",
      date: "2024-03-20",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Founder Equity"
    },
    {
      id: 2,
      title: "iSPAI Initial Seed Funding Secured",
      date: "2024-02-10",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Founder Equity"
    },
    {
      id: 3,
      title: "iSPAI Founding Team Assembly",
      date: "2024-01-25",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Founder Equity"
    },
    sharedPatentBounty
  ];

  // Corey Vickers solution bounties
  const coreyBounties: SolutionBounty[] = [
    sharedPatentBounty,
    {
      id: 1,
      title: "Infield Logistics Demo Knowledge Base Defined",
      date: "2024-03-12",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Cofounder Equity"
    },
    {
      id: 2,
      title: "iSPAI Creation-to-Consumption Frictionless Flywheel",
      date: "2024-02-18",
      status: "Accepted",
      bountyAmount: 10000,
      isShared: true,
      collaborators: [
        {
          name: "Karen Watson",
          title: "iSPAI Chief Consumer",
          profileUrl: "/myspai-profile/karen-watson",
          amount: 10000
        }
      ]
    },
    {
      id: 3,
      title: "Giblet's Ten Commandments for Creators",
      date: "2024-01-30",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Cofounder Equity"
    }
  ];

  // Miguel Escobedo solution bounties
  const miguelBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "iSPAI Product Roadmap and Strategy",
      date: "2024-03-05",
      status: "Accepted",
      bountyAmount: 28000
    },
    {
      id: 2,
      title: "iSPAI User Experience Framework",
      date: "2024-02-22",
      status: "Accepted",
      bountyAmount: 18000
    },
    {
      id: 3,
      title: "iSPAI Feature Prioritization Model",
      date: "2024-02-01",
      status: "Accepted",
      bountyAmount: 12000
    },
    sharedPatentBounty
  ];

  // Karen Watson solution bounties
  const karenBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "iSPAI Consumer Experience Strategy",
      date: "2024-03-08",
      status: "Accepted",
      bountyAmount: 5000
    },
    {
      id: 2,
      title: "iSPAI User Adoption Framework",
      date: "2024-02-15",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 3,
      title: "iSPAI Creation-to-Consumption Frictionless Flywheel",
      date: "2024-02-18",
      status: "Accepted",
      bountyAmount: 10000,
      isShared: true,
      collaborators: [
        {
          name: "Corey Vickers",
          title: "iSPAI Chief Creator",
          profileUrl: "/corey-profile",
          amount: 10000
        }
      ]
    },
    {
      id: 4,
      title: "iSPAI Call for Solutions (CfS) Fully Integrated Application",
      date: "2024-01-15",
      status: "Accepted",
      bountyAmount: 10000,
      isShared: true,
      collaborators: [
        {
          name: "Miguel Escobedo",
          title: "iSPAI Chief Product Officer",
          profileUrl: "/miguel-profile",
          amount: 10000
        }
      ]
    }
  ];

  // Yannis Kalfoglou solution bounties
  const yannisBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "iSPAI Knowledge Representation Architecture",
      date: "2024-03-18",
      status: "Accepted",
      bountyAmount: 40000
    },
    {
      id: 2,
      title: "iSPAI AI Integration Framework",
      date: "2024-02-28",
      status: "Accepted",
      bountyAmount: 32000
    },
    {
      id: 3,
      title: "iSPAI Technical Governance Model",
      date: "2024-02-08",
      status: "Accepted",
      bountyAmount: 18000
    },
    sharedPatentBounty,
    {
      id: 4,
      title: "Decentralized Storage Solution",
      date: "2024-01-15",
      status: "Accepted",
      bountyAmount: 5000,
      isShared: true,
      collaborators: [
        {
          name: "Karen Watson",
          title: "iSPAI Chief Consumer",
          profileUrl: "/karen-profile",
          amount: 5000
        }
      ]
    }
  ];

  // Jim Lawnin solution bounties
  const jimBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "iSPAI Financial Strategy and Structure",
      date: "2024-03-22",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 2,
      title: "iSPAI Venture Capital Pitch Deck",
      date: "2024-02-26",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 3,
      title: "iSPAI Financial Projections Model",
      date: "2024-02-05",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 4,
      title: "iSPAI Token Economics Framework",
      date: "2024-01-18",
      status: "Accepted",
      bountyAmount: 5000,
      isShared: true,
      collaborators: [
        {
          name: "Shaan Ray",
          title: "iSPAI Web3 Advisor",
          profileUrl: "/shaan-profile",
          amount: 5000
        }
      ]
    }
  ];

  // Jess profile solution bounties
  const jessBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "Redesign Website",
      date: "2024-03-14",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 2,
      title: "iSPAI / CURT Brand and Communication Strategy",
      date: "2024-02-24",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Retainer"
    },
    {
      id: 3,
      title: "iSPAI Social Media Branding Campaign",
      date: "2024-02-03",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Retainer"
    },
    {
      id: 4,
      title: "iSPAI Brand Package",
      date: "2024-01-15",
      status: "Accepted",
      bountyAmount: 3000
    }
  ];

  // Shaan profile solution bounties
  const shaanBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "iSPAI Web3 Strategy",
      date: "2024-03-16",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 2,
      title: "CURT POC Blockchain Feasibility Report",
      date: "2024-02-22",
      status: "Accepted",
      bountyAmount: 5000
    },
    {
      id: 3,
      title: "iSPAI Web3 Components Product",
      date: "2024-01-28",
      status: "Accepted",
      bountyAmount: 5000,
      isShared: true,
      collaborators: [
        {
          name: "Miguel Escobedo",
          title: "iSPAI Chief Product Officer",
          profileUrl: "/miguel-profile",
          amount: 5000
        }
      ]
    },
    {
      id: 4,
      title: "iSPAI Token Economics Framework",
      date: "2024-01-18",
      status: "Accepted",
      bountyAmount: 5000,
      isShared: true,
      collaborators: [
        {
          name: "Jim Lawnin",
          title: "iSPAI Financial Advisor",
          profileUrl: "/myspai-profile/jim-lawnin",
          amount: 5000
        }
      ]
    }
  ];

  // Mike Mosher profile solution bounties
  const mikeBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "iSPAI Security and Privacy Position",
      date: "2024-03-12",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 2,
      title: "iSPAI Enterprise CISO FAQs",
      date: "2024-02-25",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 3,
      title: "iSPAI Platform Security Architecture Implementation Plan for AI",
      date: "2024-02-10",
      status: "Accepted",
      bountyAmount: 5000
    },
    {
      id: 4,
      title: "iSPAI GRC Agentic Automation Model",
      date: "2024-01-20",
      status: "Accepted",
      bountyAmount: 5000
    }
  ];

  // Pete profile solution bounties
  const peteBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "Capital Project Smart Contract Playbook",
      date: "2024-03-10",
      status: "Accepted",
      bountyAmount: 20000
    },
    {
      id: 2,
      title: "Owners Safety Blueprint Modernization",
      date: "2024-02-18",
      status: "Accepted",
      bountyAmount: 15000
    },
    {
      id: 3,
      title: "iSPAI Academic Partnership Strategy",
      date: "2024-01-22",
      status: "Accepted",
      bountyAmount: 10000
    }
  ];

  // Morgan Eldred solution bounties
  const morganBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "iSPAI Information Management Framework",
      date: "2024-03-18",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    {
      id: 2,
      title: "iSPAI Member IP Protection Strategy",
      date: "2024-02-22",
      status: "In Process",
      bountyAmount: 0,
      specialValue: "100 Shares, iSPAI Common Stock, Fully Vested"
    },
    {
      id: 3,
      title: "Enterprise AI Integration Roadmap for iSPAI",
      date: "2024-02-01",
      status: "Accepted",
      bountyAmount: 0,
      specialValue: "Advisor Equity"
    },
    sharedPatentBounty
  ];

  // Julia De Rosi solution bounties
  const juliaBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "Federal Building Sustainable Construction Framework",
      date: "2024-03-20",
      status: "Accepted",
      bountyAmount: 35000
    },
    {
      id: 2,
      title: "GSA Public Building Service Modern Workplace Strategy",
      date: "2024-02-15",
      status: "Accepted",
      bountyAmount: 25000
    },
    {
      id: 3,
      title: "Federal Historic Property Adaptive Reuse Model",
      date: "2024-01-25",
      status: "Accepted",
      bountyAmount: 30000,
      isShared: true,
      collaborators: [
        {
          name: "Morgan Eldred, PhD",
          title: "iSPAI Information Advisor",
          profileUrl: "/morgan-profile",
          amount: 15000
        }
      ]
    }
  ];

  // Generic solution bounties (default)
  const genericBounties: SolutionBounty[] = [
    {
      id: 1,
      title: "AI Integration Strategy",
      date: "2024-02-15",
      status: "Accepted",
      bountyAmount: 25000
    },
    {
      id: 2,
      title: "Knowledge Base Architecture",
      date: "2024-01-30",
      status: "Accepted",
      bountyAmount: 15000
    },
    {
      id: 3,
      title: "Web3 Authentication System",
      date: "2024-01-01",
      status: "Accepted",
      bountyAmount: 10000
    }
  ];

  // Determine which bounties to display based on the userId parameter
  const getBountiesForUser = (userId: string | undefined) => {
    switch(userId) {
      case "chris":
        return chrisBounties;
      case "trey":
        return treyBounties;
      case "corey":
        return coreyBounties;
      case "miguel":
        return miguelBounties;
      case "karen":
        return karenBounties;
      case "yannis":
        return yannisBounties;
      case "jim":
        return jimBounties;
      case "jess":
        return jessBounties;
      case "shaan":
        return shaanBounties;
      case "mike":
        return mikeBounties;
      case "pete":
        return peteBounties;
      case "morgan":
        return morganBounties;
      case "julia":
        return juliaBounties;
      default:
        return genericBounties;
    }
  };

  const solutionResponses = getBountiesForUser(userId);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getUserName = (userId: string | undefined) => {
    switch(userId) {
      case "chris":
        return "Chris Clayton";
      case "trey":
        return "Trey Fleming";
      case "corey":
        return "Corey Vickers";
      case "miguel":
        return "Miguel Escobedo";
      case "karen":
        return "Karen Watson";
      case "yannis":
        return "Yannis Kalfoglou, PhD";
      case "jim":
        return "Jim Lawnin";
      case "jess":
        return "Jess Simm";
      case "shaan":
        return "Shaan Ray";
      case "mike":
        return "Mike Mosher";
      case "pete":
        return "Pete Dumont";
      case "morgan":
        return "Morgan Eldred, PhD";
      case "julia":
        return "Julia De Rosi";
      default:
        return "Team Member";
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch(status) {
      case "In Process":
        return "bg-orange-50 text-orange-700";
      case "Accepted":
      default:
        return "bg-emerald-50 text-emerald-700";
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10 text-center">
          <h1 className="font-sans text-4xl font-bold mb-3 text-slate-800">Solution Bounties</h1>
          <h2 className="text-xl text-slate-600">
            by {getUserName(userId)}
          </h2>
        </div>
        <div className="grid gap-6">
          {solutionResponses.map((response) => (
            <Card key={response.id} className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <h3 className="font-sans text-xl font-semibold text-slate-800 bg-slate-50 px-3 py-2 rounded-md">
                    {response.title}
                  </h3>
                  <p className="text-sm text-slate-500">Captured on {response.date}</p>
                  {response.isShared && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="text-sm text-blue-600 hover:text-blue-800 mt-2 inline-flex items-center">
                          <span className="underline">Fractionalized bounty</span>
                        </TooltipTrigger>
                        <TooltipContent className="p-4 max-w-sm bg-white shadow-lg rounded-lg border">
                          <h4 className="font-semibold mb-2 text-slate-800">Also awarded to:</h4>
                          {response.collaborators?.map((collaborator, index) => (
                            <div key={index} className="mb-2 border-b border-slate-100 pb-2 last:border-0">
                              <Link 
                                to={collaborator.profileUrl}
                                className="text-blue-600 hover:text-blue-800 font-medium"
                              >
                                {collaborator.name}
                              </Link>
                              <p className="text-sm text-slate-500">{collaborator.title}</p>
                              <p className="text-sm font-medium text-slate-700">{formatCurrency(collaborator.amount)}</p>
                            </div>
                          ))}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <div className="text-right">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(response.status)}`}>
                    {response.status}
                  </span>
                  {response.specialValue ? (
                    <p className="mt-2 font-semibold text-slate-800">{response.specialValue}</p>
                  ) : (
                    <p className="mt-2 font-semibold text-slate-800">{formatCurrency(response.bountyAmount)}</p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default SolutionBounties;
